import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { useNavigate } from "react-router-dom";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ClearIcon from "@mui/icons-material/Clear";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { parse } from "csv-parse/browser/esm/sync";
import { darken, lighten, styled } from "@mui/material/styles";
import Labels from "../labels/Labels";

export default function RequestCardsDialog(props) {
  const isIncentiveLikeProduct = (productId) =>
    ["INCENTIVE", "AWARD_PHYSICAL"].includes(productId);

  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(0);
  const [embossType, setEmbossType] = useState("one");
  const [tabSelected, setTabSelected] = React.useState(0);
  const [csvData, setCsvData] = useState([]);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });
  // This state is synced with formik but needed for UI control
  // eslint-disable-next-line no-unused-vars
  const [isOrderWithDenomination, setIsOrderWithDenomination] = useState(false);
  const [productSelectionStep, setProductSelectionStep] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Cart items for INCENTIVE product (shopping cart interface)
  const [cartItems, setCartItems] = useState([]);
  const [cartItemQuantity, setCartItemQuantity] = useState("");
  const [cartItemDenomination, setCartItemDenomination] = useState("");

  // Initialize refs at the top
  const dataGridRef = useRef(null);
  const formikInitialized = useRef(false);
  const dialogOpenState = useRef(false);

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    // Reset the entire feedback state, maintaining the current type
    setFeedback((prev) => ({
      ...prev,
      open: false,
    }));
  };

  async function fetchLocations() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/locations`
    );
    return response;
  }

  async function fetchEmployerInfo() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer`
    );
    return response;
  }

  async function fetchEmployerAttribute(attributeName) {
    const url = window.API_URL + `/employer/attributes/${attributeName}`;
    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }
  const {
    data: dataLocations,
    isLoading: isLoadingLocations,
    // eslint-disable-next-line no-unused-vars
    isError: isErrorLocations,
  } = useQuery(["locations"], fetchLocations);

  const {
    data: dataEmployer,
    isLoading: isFetchingEmployer,
    // eslint-disable-next-line no-unused-vars
    isError: isErrorEmployer,
  } = useQuery(["employerInfo"], fetchEmployerInfo);

  // Fetch individual product-related attributes
  const {
    data: usePaycardAttr,
    isLoading: isLoadingPaycard,
    isFetching: isFetchingPaycard,
  } = useQuery(
    ["usePaycardAttr"],
    () => fetchEmployerAttribute("USE_PAYCARD"),
    {
      keepPreviousData: false, // Don't keep previous data
      staleTime: 0, // Consider data immediately stale
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes (can adjust)
      enabled: productSelectionStep, // Only fetch during product selection step
    }
  );

  async function fetchCanOrderDenominationAttribute() {
    if (!selectedProduct) return null;

    const response = await HttpService.getAxiosClient().get(
      window.API_URL +
        `/employer/attributes/CAN_ORDER_DENOMINATION?productType=${selectedProduct.id}`
    );
    return response;
  }

  const {
    data: dataCanOrderDenomination,
    isFetching: isFetchingCanOrderDenomination,
    // Keep track of error state but don't use it directly in UI
    // eslint-disable-next-line no-unused-vars
    isError: isErrorDataCanOrderDenomination,
    refetch: refetchDenominationAttribute,
  } = useQuery(
    ["dataCanOrderDenomination", selectedProduct?.id],
    () => fetchCanOrderDenominationAttribute(),
    {
      keepPreviousData: true,
      enabled: !!selectedProduct && !productSelectionStep, // Only run when product is selected and not in selection step
    }
  );

  const canOrderMultipleDenominations =
    String(dataCanOrderDenomination?.data?.value).toLowerCase() === "true";

  // Refetch denomination data when selected product changes
  useEffect(() => {
    if (selectedProduct) {
      refetchDenominationAttribute();
    }
  }, [selectedProduct, refetchDenominationAttribute]);

  useEffect(() => {
    if (productSelectionStep) return;

    if (
      !canOrderMultipleDenominations &&
      formik.values.isOrderWithDenomination
    ) {
      setIsOrderWithDenomination(false);
      formik.setFieldValue("isOrderWithDenomination", false);
      setCartItems([]);
      setCartItemQuantity("");
      setCartItemDenomination("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productSelectionStep,
    selectedProduct?.id,
    canOrderMultipleDenominations,
  ]);

  const {
    data: useCorporateExpenseAttr,
    isLoading: isLoadingCorporateExpense,
    isFetching: isFetchingCorporateExpense,
  } = useQuery(
    ["useCorporateExpenseAttr"],
    () => fetchEmployerAttribute("USE_CORPORATE_EXPENSE"),
    {
      keepPreviousData: false, // Don't keep previous data
      staleTime: 0, // Consider data immediately stale
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes (can adjust)
      enabled: productSelectionStep, // Only fetch during product selection step
    }
  );

  const {
    data: useIncentiveAttr,
    isLoading: isLoadingIncentive,
    isFetching: isFetchingIncentive,
  } = useQuery(
    ["useIncentiveAttr"],
    () => fetchEmployerAttribute("USE_INCENTIVE"),
    {
      keepPreviousData: false, // Don't keep previous data
      staleTime: 0, // Consider data immediately stale
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes (can adjust)
      enabled: productSelectionStep, // Only fetch during product selection step
    }
  );

  const {
    data: useVirtualCardAttr,
    isLoading: isLoadingVirtualCard,
    isFetching: isFetchingVirtualCard,
  } = useQuery(
    ["useVirtualCardAttr"],
    () => fetchEmployerAttribute("USE_VIRTUAL"),
    {
      keepPreviousData: false, // Don't keep previous data
      staleTime: 0, // Consider data immediately stale
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes (can adjust)
      enabled: productSelectionStep, // Only fetch during product selection step
    }
  );

  const {
    data: useRewardsAnonymCardAttr,
    isLoading: isLoadingRewardsAnonymCard,
    isFetching: isFetchingRewardsAnonymCard,
  } = useQuery(
    ["useRewardsAnonymCardAttr"],
    () => fetchEmployerAttribute("USE_REWARDS_ANONYM"),
    {
      keepPreviousData: false, // Don't keep previous data
      staleTime: 0, // Consider data immediately stale
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes (can adjust)
      enabled: productSelectionStep, // Only fetch during product selection step
    }
  );

  const {
    data: useGprRetailAttr,
    isLoading: isLoadingGprRetail,
    isFetching: isFetchingGprRetail,
  } = useQuery(
    ["useGprRetailAttr"],
    () => fetchEmployerAttribute("USE_GPR_RETAIL"),
    {
      keepPreviousData: false,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      enabled: productSelectionStep,
    }
  );

  const { data: useAwardPhysicalAttr, isFetching: isFetchingAwardPhysical } =
    useQuery(
      ["useAwardPhysicalAttr"],
      () => fetchEmployerAttribute("USE_AWARD_PHYSICAL"),
      {
        keepPreviousData: false,
        staleTime: 0,
        cacheTime: 5 * 60 * 1000,
        enabled: productSelectionStep,
      }
    );

  // Fetch default card type attribute
  const {
    data: defaultCardTypeAttr,
    isLoading: isLoadingDefaultCardType,
    isFetching: isFetchingDefaultCardType,
  } = useQuery(
    ["defaultCardTypeAttr"],
    () => fetchEmployerAttribute("DEFAULT_CARD_TYPE"),
    {
      keepPreviousData: false, // Don't keep previous data
      staleTime: 0, // Consider data immediately stale
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes (can adjust)
      enabled: productSelectionStep, // Only fetch during product selection step
    }
  );

  // Fetch INCENTIVE fee per card attribute
  const { data: incentiveFeePerCardAttr, isLoading: isLoadingIncentiveFee } =
    useQuery(
      ["incentiveFeePerCardAttr"],
      () => fetchEmployerAttribute("INCENTIVE_FEE_PER_CARD"),
      {
        keepPreviousData: false,
        staleTime: 0,
        cacheTime: 5 * 60 * 1000,
        enabled:
          !productSelectionStep && isIncentiveLikeProduct(selectedProduct?.id),
      }
    );

  const validationSchema = yup.object({
    description: yup
      .string("Enter the description for this request")
      .required("A description is required")
      .max(30, "Max length is 30"),
    embossPart1: yup.string().when("cardType", {
      is: "REWARDS_ANONYM",
      then: yup.string().nullable().notRequired(),
      otherwise: yup
        .string("Enter the Embossing (Part 1)")
        .required("Emboss part 1 is required")
        //.max(9,"Max length is 9")
        .matches(/^[a-zA-Z\s]+$/, "Only letters and numbers are allowed")
        .test(
          "embossing_length",
          "The embossing max length is 19",
          function (value) {
            const { embossPart1 = "", embossPart2 = "" } = this.parent;
            return embossPart1.length + embossPart2.length < 18;
          }
        ),
    }),
    embossPart2: yup.string().when("cardType", {
      is: "REWARDS_ANONYM",
      then: yup.string().nullable().notRequired(),
      otherwise: yup
        .string("Enter the Embossing (Part 2)")
        .required("Emboss part 2 is required")
        //.max(9,"Max length is 9")
        .matches(/^[a-zA-Z\s]+$/, "Only letters and numbers are allowed")
        .test(
          "embossing_length",
          "The embossing lenght is calculated using the two fields",
          function (value) {
            const { embossPart1 = "", embossPart2 = "" } = this.parent;
            return embossPart1.length + embossPart2.length < 18;
          }
        ),
    }),
    embossPart3: yup.string().when("cardType", {
      is: "REWARDS_ANONYM",
      then: yup.string().nullable().notRequired(),
      otherwise: yup
        .string("Enter the Embossing (Part 3)")
        .max(19, "Max length is 19")
        .matches(
          /^[0-9a-zA-Z\s-]+$/,
          "Only letters, numbers and dashes are allowed"
        ),
    }),
    quantity: yup.number().when(["cardType", "isOrderWithDenomination"], {
      is: (cardType, isOrderWithDenomination) =>
        isIncentiveLikeProduct(cardType) && isOrderWithDenomination === true,
      then: yup.number().notRequired(),
      otherwise: yup
        .number("Enter how many cards do you want")
        .required("quantity is required")
        .min(1, "Min quantity is 1")
        .max(500, "Max quantity is 500"),
    }),
    idLocation: yup
      .string("Select the location")
      .required("Location is required")
      .test(
        "must_be_selected",
        "You need to select a location",
        (value) => value !== "0"
      ),
    amount: yup.number().when(["isOrderWithDenomination", "cardType"], {
      is: (isOrderWithDenomination, cardType) =>
        isOrderWithDenomination === true && !isIncentiveLikeProduct(cardType),
      then: yup
        .number()
        .required("Amount is required")
        .min(5, "Min amount is 5")
        .max(500, "Max amount is 500"),
      otherwise: yup.number().notRequired(),
    }),
  });

  const queryClient = useQueryClient();

  const validateEmbossing = (row) => {
    var regexFirstLineEmbossing = /^[A-Za-z]+$/;
    var regexSecondLineEmbossing = /^[A-Z a-z0-9]+$/;

    var isValid = regexFirstLineEmbossing.test(row.embossPart1);
    if (!isValid) return "Line 1 first part only allow A-Z";

    isValid = regexFirstLineEmbossing.test(row.embossPart2);
    if (row.embossPart2 && !isValid)
      return "Line 1 second part only allow A-Z (uppercase)";

    if (row?.embossPart1.length + row?.embossPart2?.length > 18)
      return "Line 1 max 19 characters";

    isValid = regexSecondLineEmbossing.test(row.embossPart3);
    if (row.embossPart3 && !isValid) return "Line 2 only allow A-Z 0-9";

    regexFirstLineEmbossing = /^[A-Z 0-9]{1,19}$/;
    isValid = regexSecondLineEmbossing.test(row.embossPart3);
    if (row.embossPart3 && !isValid) return "Line 2 max 19 characters";

    return "";
  };

  const createBundleMutation = useMutation(
    (newBundle) => {
      newBundle.embossPart3 = embossType === "two" ? newBundle.embossPart3 : "";
      return HttpService.getAxiosClient().put(
        window.API_URL + `/paycards/bundle`,
        newBundle
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["bundles"] });
        handleClose();
      },
      onError: (err) => {
        // Get error message from response or use generic message
        // eslint-disable-next-line no-unused-vars
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        //   setFeedback({
        //   open: true,
        //   type: "error",
        //   message: msg,
        // });
      },
    }
  );

  // Get initial form values safely
  const getInitialFormValues = useRef({
    description: "",
    embossPart1: "VALUED",
    embossPart2: "MEMBER",
    embossPart3: "", // Will be set when employer data is available
    quantity: "",
    idLocation: "",
    amount: "",
    isOrderWithDenomination: false,
    cardType: "", // Will be set when product is selected
  }).current;

  // Cart management functions for INCENTIVE product
  const addToCart = () => {
    const qty = parseInt(cartItemQuantity);
    const denom = parseFloat(cartItemDenomination);

    if (!qty || qty < 1 || qty > 500) {
      setFeedback({
        open: true,
        type: "error",
        message: "Quantity must be between 1 and 500",
      });
      return;
    }

    if (!denom || denom < 5 || denom > 500) {
      setFeedback({
        open: true,
        type: "error",
        message: "Denomination must be between $5 and $500",
      });
      return;
    }

    const newItem = {
      id: Date.now(),
      quantity: qty,
      denomination: denom,
      subtotal: qty * denom,
    };

    setCartItems([...cartItems, newItem]);
    setCartItemQuantity("");
    setCartItemDenomination("");
  };

  const removeFromCart = (itemId) => {
    setCartItems(cartItems.filter((item) => item.id !== itemId));
  };

  const getCartTotals = () => {
    const totalCards = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = cartItems.reduce((sum, item) => sum + item.subtotal, 0);

    // Calculate fee based on INCENTIVE_FEE_PER_CARD attribute
    const feePerCard = incentiveFeePerCardAttr?.data?.value
      ? parseFloat(incentiveFeePerCardAttr.data.value)
      : 0;

    const totalFee = totalCards * feePerCard;
    const totalWithFee = totalAmount + totalFee;

    return { totalCards, totalAmount, totalFee, totalWithFee, feePerCard };
  };

  const formik = useFormik({
    initialValues: getInitialFormValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const isRewardsAnonymous = selectedProduct?.id === "REWARDS_ANONYM";
      const isIncentiveProductSelection = isIncentiveLikeProduct(
        selectedProduct?.id
      );

      // Validate cart for INCENTIVE product with denomination
      if (isIncentiveProductSelection && values.isOrderWithDenomination) {
        if (cartItems.length === 0) {
          setFeedback({
            open: true,
            type: "error",
            message: "Please add at least one item to the cart",
          });
          return;
        }

        const { totalCards, totalAmount, totalFee, totalWithFee } =
          getCartTotals();

        // Override values for INCENTIVE with cart data
        // Preserve embossing fields from the form (embossPart1, embossPart2)
        values = {
          ...values,
          quantity: totalCards,
          cartItems: cartItems,
          totalAmount: totalWithFee, // Include fee in total amount sent to backend
          embossList: [],
        };
      } else if (
        isIncentiveProductSelection &&
        !values.isOrderWithDenomination
      ) {
        // INCENTIVE without denomination - simple quantity order
        // Preserve embossing fields entered in the form
        values = {
          ...values,
          embossList: [],
        };
      } else if (
        !isIncentiveProductSelection &&
        values.isOrderWithDenomination
      ) {
        // Non-INCENTIVE with denomination: compute totalAmount = quantity * amount
        const qty = Number(values.quantity || 0);
        const amt = Number(values.amount || 0);
        values = {
          ...values,
          totalAmount: qty * amt,
        };
      }

      if (!isRewardsAnonymous) {
        if (tabSelected === 0) {
          if (embossType === "two" && values.embossPart3 === undefined)
            values = { ...values, embossPart3: dataEmployer?.data.name };
        }
        if (tabSelected === 1) {
          if (rowsWithIndex.length === 0) {
            setFeedback({
              open: true,
              type: "error",
              message: Labels.getLabel(
                "requestCardsDialog.embossingListNotLoaded",
                "Embossing list is not loaded, please upload the embossing list in CSV"
              ),
            });
            return;
          }
          if (rowsWithIndex.length !== values.quantity) {
            setFeedback({
              open: true,
              type: "error",
              message: Labels.getLabel(
                "requestCardsDialog.quantityMismatch",
                "The quantity does not correspond to the number of lines in the uploaded CSV"
              ),
            });
            return;
          }

          if (!rowsWithIndex.every((obj) => obj.status === "")) {
            setFeedback({
              open: true,
              type: "error",
              message: Labels.getLabel(
                "requestCardsDialog.validationIssues",
                "There are issues with the validation of the lines from the CSV. Please review the status field, where the red ones refer to the ones with issues."
              ),
            });
            return;
          }

          values.embossPart1 = "";
          values.embossPart2 = "";
          values.embossPart3 = "";
          values = { ...values, embossList: rowsWithIndex };
        }
      } else {
        values = {
          ...values,
          embossPart1: "",
          embossPart2: "",
          embossPart3: "",
          embossList: [],
        };
      }
      // Add the product type to the submitted values
      if (selectedProduct) {
        values = {
          ...values,
          cardType: selectedProduct.id,
        };
      } else {
        setFeedback({
          open: true,
          type: "error",
          message: "Please select a product type before submitting",
        });
        return;
      }
      createBundleMutation.mutate({ ...values });
    },
  });

  const handleLocationChange = (event, idLocation) => {
    setSelectedLocation(idLocation.props.value);
    formik.setFieldValue("idLocation", idLocation.props.value);
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    props.setOpen(false);
  };

  const handleChangeEmbossType = (event) => {
    event.persist();
    setEmbossType(event.target.value);
  };

  // Handle dialog open/close with a single useEffect to prevent loops

  // Update form when employer data changes
  useEffect(() => {
    if (dataEmployer?.data?.name && formik && formikInitialized.current) {
      // We need to delay to avoid the circular dependency issue
      const timer = setTimeout(() => {
        formik.setFieldValue(
          "embossPart3",
          dataEmployer.data.name.substr(0, 18) || ""
        );
      }, 10);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEmployer?.data?.name]);

  useEffect(() => {
    // Only handle when dialog open state changes
    if (props.open !== dialogOpenState.current) {
      dialogOpenState.current = props.open;

      if (props.open) {
        // Dialog is opening
        setSelectedLocation("0");
        setProductSelectionStep(true);
        setSelectedProduct(null);

        // Reset form only when formik is initialized and only once
        if (formik) {
          // Mark formik as initialized
          formikInitialized.current = true;

          // Delay form reset to avoid render cycles
          const timer = setTimeout(() => {
            formik.resetForm();
          }, 10);
          return () => clearTimeout(timer);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const handleTabChange = (event, newValue) => {
    setTabSelected(newValue);
  };

  // Direct CSV upload function
  const uploadCSV = (event) => {
    event.preventDefault();
    // Clear any existing feedback before starting new upload
    setFeedback({
      open: false,
      type: "info",
      message: "",
    });

    // Create a temporary file input
    const tempFileInput = document.createElement("input");
    tempFileInput.type = "file";
    tempFileInput.accept = ".csv";
    tempFileInput.style.display = "none";
    document.body.appendChild(tempFileInput);

    // Set up the onchange handler
    tempFileInput.onchange = (e) => {
      try {
        const file = e.target.files[0];
        if (!file) {
          return;
        }

        // Read the file
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const result = evt.target.result;

            // Parse the CSV
            const cleanResult = result
              .replace(/^\ufeff/, "")
              .replace("ï»¿", "");
            const records = parse(cleanResult, {
              columns: ["embossPart1", "embossPart2", "embossPart3"],
              relax_column_count: true,
              delimiter: ",",
              trim: true,
              skip_empty_lines: true,
            });

            // Process the records to check for validation issues
            const processedRecords = records.map((row, index) => ({
              ...row,
              embossPart1: row?.embossPart1?.toUpperCase(),
              embossPart2: row?.embossPart2?.toUpperCase(),
              id: index + 1,
              status: validateEmbossing(row),
            }));

            // Update the processed records for display
            setCsvData(processedRecords);

            // Check if any records have validation issues
            const hasValidationIssues = processedRecords.some(
              (record) => record.status !== ""
            );

            if (hasValidationIssues) {
              console.warn("Some records have validation issues");
              setFeedback({
                open: true,
                type: "warning",
                message: `Imported ${records.length} record(s), but some have validation issues. Please check the highlighted rows, once fixed, please resubmit.`,
              });
            } else {
              setFeedback({
                open: true,
                type: "success",
                message: `Successfully imported ${records.length} record(s).`,
              });
            }
          } catch (err) {
            console.error("Error parsing CSV:", err);
            setFeedback({
              open: true,
              type: "error",
              message: `Error parsing CSV: ${err.message}`,
            });
          }
        };

        reader.onerror = (err) => {
          console.error("Error reading file:", err);
          setFeedback({
            open: true,
            type: "error",
            message: "Error reading file",
          });
        };

        reader.readAsBinaryString(file);
      } catch (err) {
        console.error("Error in file selection:", err);
        setFeedback({
          open: true,
          type: "error",
          message: `Error: ${err.message}`,
        });
      } finally {
        // Clean up
        document.body.removeChild(tempFileInput);
      }
    };

    // Trigger the file dialog
    tempFileInput.click();
  };

  const columns = [
    {
      field: "id",
      headerName: "#",
      width: 15,
    },
    {
      field: "embossPart1",
      headerName: Labels.getLabel(
        "requestCardsDialog.line1FirstPart",
        "Line 1 (first part)"
      ),
      width: 130,
    },
    {
      field: "embossPart2",
      headerName: Labels.getLabel(
        "requestCardsDialog.line1SecondPart",
        "Line 1 (second part)"
      ),
      width: 130,
    },
    {
      field: "embossPart3",
      headerName: Labels.getLabel("requestCardsDialog.line2", "Line 2"),
      width: 230,
    },
    {
      field: "status",
      headerName: Labels.getLabel("requestCardsDialog.status", "Status"),
      width: 300,
    },
  ];

  const rowsWithIndex = csvData.map((row, index) => ({
    ...row,
    embossPart1: row?.embossPart1?.toUpperCase(),
    embossPart2: row?.embossPart2?.toUpperCase(),
    id: index + 1,
    status: validateEmbossing(row),
  })); // Add id property using index

  const renderEmbossing = () => {
    const getBackgroundColor = (color, mode) =>
      mode === "dark" ? darken(color, 0.7) : lighten(color, 0.7);

    const getHoverBackgroundColor = (color, mode) =>
      mode === "dark" ? darken(color, 0.6) : lighten(color, 0.6);

    const getSelectedBackgroundColor = (color, mode) =>
      mode === "dark" ? darken(color, 0.5) : lighten(color, 0.5);

    const getSelectedHoverBackgroundColor = (color, mode) =>
      mode === "dark" ? darken(color, 0.4) : lighten(color, 0.4);

    const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
      "& .super-app-theme--Rejected": {
        backgroundColor: getBackgroundColor(
          theme.palette.error.main,
          theme.palette.mode
        ),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(
            theme.palette.error.main,
            theme.palette.mode
          ),
        },
        "&.Mui-selected": {
          backgroundColor: getSelectedBackgroundColor(
            theme.palette.error.main,
            theme.palette.mode
          ),
          "&:hover": {
            backgroundColor: getSelectedHoverBackgroundColor(
              theme.palette.error.main,
              theme.palette.mode
            ),
          },
        },
      },
    }));

    function CustomToolbar() {
      return (
        <GridToolbarContainer>
          <GridToolbarExport />
        </GridToolbarContainer>
      );
    }

    return (
      <Fragment>
        <Grid item xs={12}>
          <div
            style={{
              width: "100%",
              border: "whitesmoke solid 1px",
              marginRight: "10px",
              height: "1px",
            }}
          ></div>
        </Grid>
        {dataEmployer !== null && dataEmployer !== undefined ? (
          <Fragment>
            <Grid container>
              <Grid item xs={12}>
                <Box>
                  <Tabs
                    value={tabSelected}
                    onChange={handleTabChange}
                    aria-label="basic tabs"
                  >
                    <Tab
                      label={Labels.getLabel(
                        "requestCardsDialog.standardEmbossing",
                        "Standard embossing"
                      )}
                    />
                    {!(
                      isIncentiveLikeProduct(selectedProduct?.id) &&
                      formik.values.isOrderWithDenomination
                    ) && (
                      <Tab
                        label={Labels.getLabel(
                          "requestCardsDialog.personalizedEmbossing",
                          "Personalized embossing"
                        )}
                      />
                    )}
                  </Tabs>
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ flex: "1 1 auto" }}>
                <div hidden={tabSelected !== 1}>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    sx={{ marginRight: "1rem" }}
                    onClick={uploadCSV} // Use the direct upload function
                    type="button" // Explicitly set type to button to avoid form submission
                  >
                    {Labels.getLabel(
                      "requestCardsDialog.uploadCSV",
                      "Upload CSV"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    sx={{ marginRight: "1rem" }}
                    onClick={(event) => {
                      event.preventDefault(); // Prevent form submission
                      setCsvData([]);

                      // Notify user
                      setFeedback({
                        open: true,
                        type: "info",
                        message: Labels.getLabel(
                          "requestCardsDialog.csvCleared",
                          "CSV data has been cleared."
                        ),
                      });
                    }}
                    type="button" // Explicitly set type to button
                  >
                    {Labels.getLabel("requestCardsDialog.clearButton", "Clear")}
                  </Button>
                </div>
                <div
                  role="tabpanel"
                  hidden={tabSelected !== 1}
                  id={`simple-tabpanel-1`}
                  aria-labelledby={`simple-tab-1`}
                >
                  {tabSelected === 1 && (
                    <Box>
                      <Grid container>
                        <Grid item xs={12}></Grid>
                        <Grid item xs={12} height="28rem">
                          {csvData ? (
                            <StyledDataGrid
                              density="compact"
                              rows={rowsWithIndex}
                              columns={columns}
                              getRowId={(row) => row.id}
                              pageSize={100}
                              rowsPerPageOptions={[100]}
                              disableSelectionOnClick
                              editable={false}
                              ref={dataGridRef}
                              components={{ Toolbar: CustomToolbar }}
                              componentsProps={{
                                toolbar: {
                                  showQuickFilter: false,
                                  quickFilterProps: { debounceMs: 800 },
                                },
                              }}
                              getRowClassName={(params) => {
                                if (params.row.status) {
                                  return "super-app-theme--Rejected";
                                }
                                return "";
                              }}
                            />
                          ) : (
                            <></>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </div>
              </Grid>
              <Grid item xs={12}>
                <div
                  role="tabpanel"
                  hidden={tabSelected !== 0}
                  id={`simple-tabpanel-0`}
                  aria-labelledby={`simple-tab-0`}
                >
                  {tabSelected === 0 && (
                    <Box>
                      <Grid container>
                        <Grid item xs={12}>
                          <FormControl>
                            <RadioGroup
                              style={{ fontSize: "8px", marginLeft: "10px" }}
                              aria-labelledby="emboss-radio-buttons-group-label"
                              defaultValue="two"
                              value={embossType}
                              row
                              name="radio-buttons-group"
                              fontSize="10px"
                              onChange={handleChangeEmbossType}
                              disabled={formik.values.isOrderWithDenomination}
                            >
                              <FormControlLabel
                                sx={{
                                  "& .MuiTypography-root": { fontSize: 15 },
                                }}
                                value="one"
                                control={
                                  <Radio
                                    sx={{
                                      "& .MuiSvgIcon-root": { fontSize: 18 },
                                    }}
                                    disabled={
                                      formik.values.isOrderWithDenomination
                                    }
                                  />
                                }
                                label={Labels.getLabel(
                                  "requestCardsDialog.oneLine",
                                  "One Line"
                                )}
                              />
                              <FormControlLabel
                                sx={{
                                  "& .MuiTypography-root": { fontSize: 15 },
                                }}
                                value={
                                  formik.values.isOrderWithDenomination
                                    ? "one"
                                    : "two"
                                }
                                control={
                                  <Radio
                                    sx={{
                                      "& .MuiSvgIcon-root": { fontSize: 18 },
                                    }}
                                    disabled={
                                      formik.values.isOrderWithDenomination
                                    }
                                  />
                                }
                                label={Labels.getLabel(
                                  "requestCardsDialog.twoLines",
                                  "Two lines"
                                )}
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            fullWidth
                            id="embossPart1"
                            label={Labels.getLabel(
                              "requestCardsDialog.embossLine1",
                              "Emboss (Line 1)"
                            )}
                            value={formik.values.embossPart1}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.embossPart1 &&
                              Boolean(formik.errors.embossPart1)
                            }
                            helperText={
                              formik.touched.embossPart1 &&
                              formik.errors.embossPart1
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            fullWidth
                            id="embossPart2"
                            label={Labels.getLabel(
                              "requestCardsDialog.embossLine1",
                              "Emboss (Line 1)"
                            )}
                            value={formik.values.embossPart2}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.embossPart2 &&
                              Boolean(formik.errors.embossPart2)
                            }
                            helperText={
                              formik.touched.embossPart2 &&
                              formik.errors.embossPart2
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ pt: 2 }}>
                          <div
                            hidden={
                              embossType !== "two" ||
                              formik.values.isOrderWithDenomination
                            }
                          >
                            <TextField
                              size="small"
                              fullWidth
                              id="embossPart3"
                              label={Labels.getLabel(
                                "requestCardsDialog.embossLine2",
                                "Emboss (Line 2)"
                              )}
                              value={formik.values.embossPart3}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.embossPart3 &&
                                Boolean(formik.errors.embossPart3)
                              }
                              helperText={
                                formik.touched.embossPart3 &&
                                formik.errors.embossPart3
                              }
                            />
                          </div>
                        </Grid>
                        <div style={{ padding: "20px" }}>
                          <CreditCardIcon
                            style={{ marginRight: "10px" }}
                            color="primary"
                          />
                          <span>
                            {Labels.getLabel(
                              "requestCardsDialog.cardInfoNote",
                              "This information will be printed in the back of the card, as additional information."
                            )}
                          </span>
                        </div>
                        <Grid item xs={12}>
                          <div
                            style={{
                              width: "100%",
                              border: "whitesmoke solid 1px",
                              marginRight: "10px",
                              height: "1px",
                            }}
                          ></div>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </div>
              </Grid>
            </Grid>
          </Fragment>
        ) : (
          ""
        )}
      </Fragment>
    );
  };

  const dialogStyles = {
    width: "100vw",
    height: "100vh",
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Product selection component
  const renderProductSelection = () => {
    // Get available products based on employer attributes
    const getAvailableProducts = () => {
      const products = [];

      // Check if we have data from any of the attribute queries
      if (
        !usePaycardAttr?.data &&
        usePaycardAttr?.data !== "" &&
        !useCorporateExpenseAttr?.data &&
        useCorporateExpenseAttr?.data !== "" &&
        !useIncentiveAttr?.data &&
        useIncentiveAttr?.data !== "" &&
        !useVirtualCardAttr?.data &&
        useVirtualCardAttr?.data !== "" &&
        !useRewardsAnonymCardAttr?.data &&
        useRewardsAnonymCardAttr?.data !== "" &&
        !useGprRetailAttr?.data &&
        useGprRetailAttr?.data !== "" &&
        !useAwardPhysicalAttr?.data &&
        useAwardPhysicalAttr?.data !== ""
      ) {
        return products;
      }

      // Default products to show if no specific attributes are found
      const defaultProducts = [
        {
          id: "PAYCARD",
          name: Labels.getLabel("requestCardsDialog.paycard", "Paycard"),
          icon: <CreditCardIcon sx={{ fontSize: 40 }} />,
          description: Labels.getLabel(
            "requestCardsDialog.paycardDesc",
            "Standard paycard for payroll distribution."
          ),
          restrictions: Labels.getLabel(
            "requestCardsDialog.paycardRestrictions",
            "Has cash access, and is reloadable. Will require personal information like date of birth, name, and e-mail address"
          ),
        },
        {
          id: "CORPORATE_EXPENSE",
          name: Labels.getLabel(
            "requestCardsDialog.corporateExpense",
            "Corporate Expense"
          ),
          icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
          description: Labels.getLabel(
            "requestCardsDialog.corporateExpenseDesc",
            "Card for managing corporate expenses and reimbursements."
          ),
          restrictions: Labels.getLabel(
            "requestCardsDialog.corporateExpenseRestrictions",
            "Cards issued to employees for things like travel, meals, or business expenses"
          ),
        },
        {
          id: "INCENTIVE",
          name: Labels.getLabel("requestCardsDialog.incentive", "Incentive"),
          icon: <CardGiftcardIcon sx={{ fontSize: 40 }} />,
          description: Labels.getLabel(
            "requestCardsDialog.incentiveDesc",
            "Rewards and incentives program cards."
          ),
          restrictions: Labels.getLabel(
            "requestCardsDialog.incentiveRestrictions",
            "Does not have cash access. Does not require any personal information regarding the cardholder"
          ),
        },
        {
          id: "VIRTUAL",
          name: Labels.getLabel("requestCardsDialog.virtual", "Virtual Card"),
          icon: <PhoneIphoneIcon sx={{ fontSize: 40 }} />,
          description: Labels.getLabel(
            "requestCardsDialog.virtualDesc",
            "Digital card for online and mobile payments."
          ),
          restrictions: Labels.getLabel(
            "requestCardsDialog.virtualRestrictions",
            "Does not have cash access. Sent via e-mail or SMS to the cardholder, will require a name, date of birth, and e-mail/phone number"
          ),
        },
        {
          id: "REWARDS_ANONYM",
          name: Labels.getLabel(
            "requestCardsDialog.rewardsAnonym",
            "Rewards Anonymous Card"
          ),
          icon: <PhoneIphoneIcon sx={{ fontSize: 40 }} />,
          description: Labels.getLabel(
            "requestCardsDialog.rewardsAnonymDesc",
            "Anonymous virtual card for online and mobile payments."
          ),
          restrictions: Labels.getLabel(
            "requestCardsDialog.rewardsAnonymRestrictions",
            "Does not have cash access. Shared securely to the cardholder, requires an external identifier but will not require personal information"
          ),
        },
        {
          id: "GPR_RETAIL",
          name: Labels.getLabel("requestCardsDialog.gprRetail", "GPR Retail"),
          icon: <CreditCardIcon sx={{ fontSize: 40 }} />,
          description: Labels.getLabel(
            "requestCardsDialog.gprRetailDesc",
            "General Purpose Reloadable retail card."
          ),
          restrictions: Labels.getLabel(
            "requestCardsDialog.gprRetailRestrictions",
            "Has cash access and is reloadable. Suitable for retail distribution."
          ),
        },
        {
          id: "AWARD_PHYSICAL",
          name: Labels.getLabel(
            "requestCardsDialog.awardPhysical",
            "Award Physical"
          ),
          icon: <CardGiftcardIcon sx={{ fontSize: 40 }} />,
          description: Labels.getLabel(
            "requestCardsDialog.awardPhysicalDesc",
            "Physical rewards card for incentive and award distribution."
          ),
          restrictions: Labels.getLabel(
            "requestCardsDialog.awardPhysicalRestrictions",
            "Does not require personal information for issuance."
          ),
        },
      ];

      // Get attribute values from individual queries
      const usePaycard = usePaycardAttr?.data?.value === "true";
      const useCorporateExpense =
        useCorporateExpenseAttr?.data?.value === "true";
      const useIncentive = useIncentiveAttr?.data?.value === "true";
      const useVirtualCard = useVirtualCardAttr?.data?.value === "true";
      const useRewardsAnonym = useRewardsAnonymCardAttr?.data?.value === "true";
      const useGprRetail = useGprRetailAttr?.data?.value === "true";
      const useAwardPhysical = useAwardPhysicalAttr?.data?.value === "true";

      // Check if any product attributes are defined
      const hasProductAttributes =
        (usePaycard !== undefined && usePaycard === true) ||
        (useCorporateExpense !== undefined && useCorporateExpense === true) ||
        (useIncentive !== undefined && useIncentive === true) ||
        (useVirtualCard !== undefined && useVirtualCard === true) ||
        (useRewardsAnonym !== undefined && useRewardsAnonym === true) ||
        (useGprRetail !== undefined && useGprRetail === true) ||
        (useAwardPhysical !== undefined && useAwardPhysical === true);
      // If no product attributes are defined, check for DEFAULT_CARD_TYPE
      if (!hasProductAttributes) {
        const defaultCardType = defaultCardTypeAttr?.data?.value;

        // If DEFAULT_CARD_TYPE is defined, return only that product
        if (defaultCardType) {
          const defaultProduct = defaultProducts.find(
            (p) => p.id === defaultCardType
          );
          return defaultProduct ? [defaultProduct] : defaultProducts;
        }
        // If no DEFAULT_CARD_TYPE, use PAYCARD as fallback
        //const paycardProduct = defaultProducts.find((p) => p.id === "PAYCARD");
        //return paycardProduct ? [paycardProduct] : defaultProducts;
      }

      // Add products based on attribute values
      if (usePaycard) {
        products.push(defaultProducts.find((p) => p.id === "PAYCARD"));
      }

      if (useCorporateExpense) {
        products.push(
          defaultProducts.find((p) => p.id === "CORPORATE_EXPENSE")
        );
      }

      if (useIncentive) {
        products.push(defaultProducts.find((p) => p.id === "INCENTIVE"));
      }

      if (useVirtualCard) {
        products.push(defaultProducts.find((p) => p.id === "VIRTUAL"));
      }

      if (useRewardsAnonym) {
        products.push(defaultProducts.find((p) => p.id === "REWARDS_ANONYM"));
      }

      if (useGprRetail) {
        products.push(defaultProducts.find((p) => p.id === "GPR_RETAIL"));
      }

      if (useAwardPhysical) {
        products.push(defaultProducts.find((p) => p.id === "AWARD_PHYSICAL"));
      }

      return products;
    };

    const handleProductSelect = (product) => {
      // For Virtual Card, redirect to assignOnTheFly instead
      if (product.id === "VIRTUAL") {
        handleClose();
        // Use setTimeout to ensure dialog is closed before navigation
        setTimeout(() => {
          // Add query parameters for the virtual card flow
          const employerName = dataEmployer?.data?.name
            ? encodeURIComponent(dataEmployer.data.name)
            : "";
          // Use React Router's navigate instead of window.location.href
          navigate(
            `/assignCardOnTheFly?issuanceType=VIRTUAL&employer=${employerName}`
          );
        }, 100);
        return;
      }

      // For other card types, continue with normal flow
      // First update UI state
      setSelectedProduct(product);
      setProductSelectionStep(false);
      setTabSelected(0);

      // Reset denomination checkbox when product changes
      setIsOrderWithDenomination(false);

      // Reset cart items when product changes
      setCartItems([]);
      setCartItemQuantity("");
      setCartItemDenomination("");

      // Then update form values using setTimeout to break potential cycles
      setTimeout(() => {
        // Batch the state updates to avoid multiple renders
        const updatedFormValues = {
          cardType: product.id,
          isOrderWithDenomination: false,
          amount: "",
        };

        // Update formik values at once
        formik.setValues({
          ...formik.values,
          ...updatedFormValues,
        });
      }, 0);
    };

    const products = getAvailableProducts();

    return (
      <Fragment>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            mx: 0,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="div" color="white">
              {Labels.getLabel("bundles.RequestDialogTitle", "Request Cards")}
            </Typography>
            <ShoppingCartIcon sx={{ color: "white" }} />
          </Box>
          <Box>
            <IconButton
              edge="end"
              color="inherit"
              size="small"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        </Box>
        <DialogContent
          sx={{
            height: "82%",
            display: "flex",
            flexDirection: "column",
            paddingTop: 3,
          }}
        >
          <DialogContentText>
            {Labels.getLabel(
              "requestCardsDialog.selectProductPrompt",
              "Please select the type of product you would like to request:"
            )}
          </DialogContentText>
          <br />
          <Grid
            container
            spacing={3}
            sx={{
              mt: 1,
              justifyContent: products.length === 1 ? "center" : "flex-start",
            }}
          >
            {products.map((product) => (
              <Grid
                item
                xs={12}
                sm={products.length === 1 ? 6 : 6}
                key={product.id}
              >
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      boxShadow: 3,
                      borderColor: "primary.main",
                    },
                  }}
                  onClick={() => handleProductSelect(product)}
                >
                  <Box sx={{ color: "primary.main", mb: 2 }}>
                    {product.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight={"bold"}
                  >
                    {product.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize={12}
                    fontWeight={"bold"}
                    sx={{ mt: 1 }}
                  >
                    {product?.restrictions}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Fragment>
    );
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      disableEscapeKeyDown={true}
      PaperProps={{
        style: dialogStyles,
        sx: { maxWidth: "800px", width: "800px", p: 0 },
      }}
    >
      {isFetchingEmployer ||
      isLoadingLocations ||
      isFetchingPaycard ||
      isFetchingCorporateExpense ||
      isFetchingIncentive ||
      isFetchingVirtualCard ||
      isFetchingRewardsAnonymCard ||
      isFetchingAwardPhysical ||
      isFetchingCanOrderDenomination ||
      isFetchingDefaultCardType ? (
        <CircularProgress color="inherit" />
      ) : (
        <Fragment>
          {productSelectionStep ? (
            renderProductSelection()
          ) : (
            <Fragment>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "primary.main",
                  px: 3,
                  minHeight: 52,
                  mx: 0,
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" component="div" color="white">
                    {props.dialogTitle} - {selectedProduct?.name}
                  </Typography>
                  {createBundleMutation.isLoading ? (
                    <CircularProgress sx={{ color: "white" }} size={20} />
                  ) : (
                    <ShoppingCartIcon sx={{ color: "white" }} />
                  )}
                </Box>
                <Box>
                  <IconButton
                    edge="end"
                    color="inherit"
                    size="small"
                    onClick={handleClose}
                    aria-label="close"
                  >
                    <CloseIcon sx={{ color: "white" }} />
                  </IconButton>
                </Box>
              </Box>
              <DialogContent
                sx={{
                  height: "82%",
                  display: "flex",
                  flexDirection: "column",
                  paddingTop: 3,
                }}
              >
                <Box
                  component="form"
                  noValidate
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault(); // Prevent default form submission
                    formik.handleSubmit(e); // Use formik's submission handler
                  }}
                >
                  <Stack spacing={2}>
                    <Alert severity="info">
                      {Labels.getLabel(
                        "requestCardsDialog.expireCardsPrompt",
                        "Note: These cards will expire approximately three years after placing this order"
                      )}
                    </Alert>

                    <DialogContentText>
                      {props.dialogContentText}
                    </DialogContentText>
                  </Stack>

                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          id="description"
                          label={Labels.getLabel(
                            "requestCardsDialog.descriptionLabel",
                            "Description"
                          )}
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.description &&
                            Boolean(formik.errors.description)
                          }
                          helperText={
                            formik.touched.description &&
                            formik.errors.description
                          }
                        />
                      </Grid>

                      {/* INCENTIVE product handling */}
                      {isIncentiveLikeProduct(selectedProduct?.id) ? (
                        <>
                          {/* Denomination checkbox for INCENTIVE */}
                          {canOrderMultipleDenominations && (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Checkbox
                                  id="hasDenominationIncentive"
                                  checked={
                                    formik.values.isOrderWithDenomination
                                  }
                                  onChange={() => {
                                    const newValue =
                                      !formik.values.isOrderWithDenomination;
                                    setIsOrderWithDenomination(newValue);
                                    formik.setFieldValue(
                                      "isOrderWithDenomination",
                                      newValue
                                    );
                                    // Clear cart when toggling
                                    if (!newValue) {
                                      setCartItems([]);
                                      setCartItemQuantity("");
                                      setCartItemDenomination("");
                                    }
                                  }}
                                  inputProps={{ "aria-label": "controlled" }}
                                />
                                <Typography variant="body2">
                                  {Labels.getLabel(
                                    "requestCardsDialog.orderWithDenomination",
                                    "Order cards with different denominations (multiple amounts)"
                                  )}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          {/* Show cart only if denomination checkbox is checked */}
                          {canOrderMultipleDenominations &&
                          formik.values.isOrderWithDenomination ? (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  p: 2,
                                  backgroundColor: "background.paper",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ mb: 2, fontWeight: "bold" }}
                                >
                                  <AddShoppingCartIcon
                                    sx={{ verticalAlign: "middle", mr: 1 }}
                                  />
                                  {Labels.getLabel(
                                    "requestCardsDialog.addCardsToCart",
                                    "Add Cards to Order"
                                  )}
                                </Typography>

                                {/* Add item form */}
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={5}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      label={Labels.getLabel(
                                        "requestCardsDialog.cardQuantity",
                                        "Quantity"
                                      )}
                                      type="number"
                                      value={cartItemQuantity}
                                      onChange={(e) =>
                                        setCartItemQuantity(e.target.value)
                                      }
                                      inputProps={{ min: 1, max: 500 }}
                                      placeholder="1-500"
                                    />
                                  </Grid>
                                  <Grid item xs={5}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      label={Labels.getLabel(
                                        "requestCardsDialog.denomination",
                                        "Denomination ($)"
                                      )}
                                      type="number"
                                      value={cartItemDenomination}
                                      onChange={(e) =>
                                        setCartItemDenomination(e.target.value)
                                      }
                                      inputProps={{
                                        min: 5,
                                        max: 500,
                                        step: 0.01,
                                      }}
                                      placeholder="5-500"
                                    />
                                  </Grid>
                                  <Grid item xs={2}>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      onClick={addToCart}
                                      fullWidth
                                      sx={{ height: "40px" }}
                                    >
                                      {Labels.getLabel(
                                        "requestCardsDialog.add",
                                        "Add"
                                      )}
                                    </Button>
                                  </Grid>
                                </Grid>

                                {/* Cart items table */}
                                {cartItems.length > 0 ? (
                                  <>
                                    <Box
                                      sx={{
                                        border: 1,
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "2fr 2fr 2fr 1fr",
                                          backgroundColor: "primary.main",
                                          color: "white",
                                          p: 1,
                                          fontWeight: "bold",
                                        }}
                                      >
                                        <Box>
                                          {Labels.getLabel(
                                            "requestCardsDialog.quantity",
                                            "Quantity"
                                          )}
                                        </Box>
                                        <Box>
                                          {Labels.getLabel(
                                            "requestCardsDialog.denomination",
                                            "Denomination"
                                          )}
                                        </Box>
                                        <Box>
                                          {Labels.getLabel(
                                            "requestCardsDialog.subtotal",
                                            "Subtotal"
                                          )}
                                        </Box>
                                        <Box></Box>
                                      </Box>
                                      {cartItems.map((item) => (
                                        <Box
                                          key={item.id}
                                          sx={{
                                            display: "grid",
                                            gridTemplateColumns:
                                              "2fr 2fr 2fr 1fr",
                                            p: 1,
                                            borderBottom: 1,
                                            borderColor: "divider",
                                            "&:last-child": { borderBottom: 0 },
                                            alignItems: "center",
                                          }}
                                        >
                                          <Box>{item.quantity}</Box>
                                          <Box>
                                            ${item.denomination.toFixed(2)}
                                          </Box>
                                          <Box>${item.subtotal.toFixed(2)}</Box>
                                          <Box>
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() =>
                                                removeFromCart(item.id)
                                              }
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>

                                    {/* Cart fee row */}
                                    {getCartTotals().feePerCard > 0 && (
                                      <Box
                                        sx={{
                                          mt: 2,
                                          p: 1.5,
                                          backgroundColor: "grey.100",
                                          borderRadius: 1,
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          border: 1,
                                          borderColor: "grey.300",
                                        }}
                                      >
                                        <Box sx={{ color: "text.secondary" }}>
                                          {Labels.getLabel(
                                            "requestCardsDialog.processingFee",
                                            "Processing Fee"
                                          )}
                                          {" ("}
                                          {getCartTotals().totalCards}
                                          {" cards × $"}
                                          {getCartTotals().feePerCard.toFixed(
                                            2
                                          )}
                                          {")"}
                                        </Box>
                                        <Box
                                          sx={{
                                            fontWeight: "medium",
                                            color: "text.primary",
                                          }}
                                        >
                                          ${getCartTotals().totalFee.toFixed(2)}
                                        </Box>
                                      </Box>
                                    )}

                                    {/* Cart totals */}
                                    <Box
                                      sx={{
                                        mt: 2,
                                        p: 2,
                                        backgroundColor: "action.hover",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          mb: 1,
                                        }}
                                      >
                                        <Box>
                                          {Labels.getLabel(
                                            "requestCardsDialog.totalCards",
                                            "Total Cards"
                                          )}
                                          : {getCartTotals().totalCards}
                                        </Box>
                                        <Box>
                                          {Labels.getLabel(
                                            "requestCardsDialog.subtotalAmount",
                                            "Subtotal"
                                          )}
                                          : $
                                          {getCartTotals().totalAmount.toFixed(
                                            2
                                          )}
                                        </Box>
                                      </Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          fontWeight: "bold",
                                          pt: 1,
                                          borderTop: 1,
                                          borderColor: "divider",
                                        }}
                                      >
                                        <Box>
                                          {Labels.getLabel(
                                            "requestCardsDialog.totalAmount",
                                            "Total Amount"
                                          )}
                                        </Box>
                                        <Box>
                                          $
                                          {getCartTotals().totalWithFee.toFixed(
                                            2
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  </>
                                ) : (
                                  <Box
                                    sx={{
                                      textAlign: "center",
                                      py: 3,
                                      color: "text.secondary",
                                    }}
                                  >
                                    <ShoppingCartIcon
                                      sx={{ fontSize: 48, opacity: 0.3 }}
                                    />
                                    <Typography variant="body2">
                                      {Labels.getLabel(
                                        "requestCardsDialog.emptyCart",
                                        "Order is empty. Add cards above."
                                      )}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          ) : (
                            /* Show regular quantity field if denomination not checked */
                            <Grid item xs={4}>
                              <TextField
                                size="small"
                                fullWidth
                                id="quantity"
                                label={Labels.getLabel(
                                  "requestCardsDialog.quantityLabel",
                                  "Quantity"
                                )}
                                type="number"
                                value={formik.values.quantity}
                                onChange={formik.handleChange}
                                error={
                                  formik.touched.quantity &&
                                  Boolean(formik.errors.quantity)
                                }
                                helperText={
                                  formik.touched.quantity &&
                                  formik.errors.quantity
                                }
                              />
                            </Grid>
                          )}

                          <Grid
                            item
                            xs={formik.values.isOrderWithDenomination ? 12 : 8}
                          >
                            <FormControl
                              error={
                                formik.touched.idLocation &&
                                Boolean(formik.errors.idLocation)
                              }
                            >
                              <InputLabel id="idLocation">
                                {Labels.getLabel(
                                  "requestCardsDialog.locationLabel",
                                  "Location"
                                )}{" "}
                              </InputLabel>
                              <Select
                                style={{ height: "40px", width: "200px" }}
                                labelId="idLocation"
                                id="simple-select"
                                label={Labels.getLabel(
                                  "requestCardsDialog.locationLabel",
                                  "Location"
                                )}
                                onChange={handleLocationChange}
                                value={selectedLocation}
                              >
                                <MenuItem value={0}>-</MenuItem>
                                {dataLocations?.data.map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item.description}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                {formik.touched.idLocation &&
                                  formik.errors.idLocation}
                              </FormHelperText>
                            </FormControl>
                          </Grid>
                        </>
                      ) : (
                        /* Non-INCENTIVE products */
                        <>
                          <Grid item xs={4}>
                            <TextField
                              size="small"
                              fullWidth
                              id="quantity"
                              label={Labels.getLabel(
                                "requestCardsDialog.quantityLabel",
                                "Quantity"
                              )}
                              type="number"
                              value={formik.values.quantity}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.quantity &&
                                Boolean(formik.errors.quantity)
                              }
                              helperText={
                                formik.touched.quantity &&
                                formik.errors.quantity
                              }
                            />
                          </Grid>
                          <Grid item xs={8}>
                            <FormControl
                              error={
                                formik.touched.idLocation &&
                                Boolean(formik.errors.idLocation)
                              }
                            >
                              <InputLabel id="idLocation">
                                {Labels.getLabel(
                                  "requestCardsDialog.locationLabel",
                                  "Location"
                                )}{" "}
                              </InputLabel>
                              <Select
                                style={{ height: "40px", width: "200px" }}
                                labelId="idLocation"
                                id="simple-select"
                                label={Labels.getLabel(
                                  "requestCardsDialog.locationLabel",
                                  "Location"
                                )}
                                onChange={handleLocationChange}
                                value={selectedLocation}
                              >
                                <MenuItem value={0}>-</MenuItem>
                                {dataLocations?.data.map((item) => (
                                  <MenuItem key={item.id} value={item.id}>
                                    {item.description}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                {formik.touched.idLocation &&
                                  formik.errors.idLocation}
                              </FormHelperText>
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      {canOrderMultipleDenominations &&
                        !isIncentiveLikeProduct(selectedProduct?.id) && (
                          <Grid item xs={10}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Checkbox
                                id="hasDenomination"
                                checked={formik.values.isOrderWithDenomination}
                                onChange={() => {
                                  const newValue =
                                    !formik.values.isOrderWithDenomination;
                                  setIsOrderWithDenomination(newValue);
                                  formik.setFieldValue(
                                    "isOrderWithDenomination",
                                    newValue
                                  );

                                  // If checkbox is selected, set emboss type to "one"
                                  // and clear the second line embossing
                                  if (newValue) {
                                    setEmbossType("one");
                                    formik.setFieldValue("embossPart3", "");
                                  }
                                }}
                                inputProps={{ "aria-label": "controlled" }}
                              />
                              <Typography variant="body2">
                                {Labels.getLabel(
                                  "requestCardsDialog.preloadAmountText",
                                  "The cards should be preloaded with a specific amount."
                                )}
                              </Typography>
                            </Box>
                          </Grid>
                        )}

                      {formik.values.isOrderWithDenomination &&
                        !isIncentiveLikeProduct(selectedProduct?.id) && (
                          <Grid item xs={5}>
                            <TextField
                              size="small"
                              fullWidth
                              id="amount"
                              label={Labels.getLabel(
                                "requestCardsDialog.enterAmountLabel",
                                "Please enter the amount"
                              )}
                              type="number"
                              value={formik.values.amount}
                              onChange={formik.handleChange}
                              error={
                                formik.touched.amount &&
                                Boolean(formik.errors.amount)
                              }
                              helperText={
                                formik.touched.amount && formik.errors.amount
                              }
                            />
                          </Grid>
                        )}

                      {selectedProduct?.id !== "REWARDS_ANONYM" &&
                        renderEmbossing()}
                    </Grid>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      pt: 2,
                    }}
                  >
                    <Button
                      onClick={() => setProductSelectionStep(true)}
                      color="primary"
                      variant="outlined"
                    >
                      {Labels.getLabel(
                        "requestCardsDialog.backToProducts",
                        "Back to Products"
                      )}
                    </Button>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        onClick={handleClose}
                        color="error"
                        variant="outlined"
                      >
                        {Labels.getLabel(
                          "requestCardsDialog.cancelButton",
                          "Cancel"
                        )}
                      </Button>
                      <Button type="submit" variant="contained">
                        {props.action}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </DialogContent>
            </Fragment>
          )}
        </Fragment>
      )}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
